/**
 * Iter-112 — MissionNotifications cell

 * Reusable across listings (Missions list, CollaboratorDetail, dashboards…).
 * Renders a compact list of small chips with tooltips. When there's no signal,
 * renders an em-dash so the column never collapses to zero width.
 */
import React from "react";
import { AlertTriangle, FileWarning, Globe, ClipboardCheck, FileSignature } from "lucide-react";

const BADGE = "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono shrink-0 border";

/**
 * Iter-113 — single source of truth for "what notifications a mission has".
 * Returns an array of notif keys present on the mission, ordered by severity:
 *   po_missing (most severe) > no_contract > client_pending > po_only > no_country
 * Used for sort + filter on the Missions list.
 */
export function missionNotifKeys(m) {
  const out = [];
  // Iter-195n — Closed missions are archives, not actionable. Silence ALL
  // notifs on them so they don't add noise to the missions list, sort
  // weights, or pending-action counts.
  if (m?.status === "CLOSED") return out;
  if (Array.isArray(m?.po_missing_roles) && m.po_missing_roles.length > 0) out.push("po_missing");
  if (m?.client_status === "PENDING_VALIDATION") out.push("client_pending");
  if (m?.client_status !== "PENDING_VALIDATION" && m?.client_has_contract === false) {
    out.push(m?.client_purchase_order_only ? "po_only" : "no_contract");
  }
  if (!m?.country || m.country === "") out.push("no_country");
  return out;
}

const NOTIF_SEVERITY = { po_missing: 5, no_contract: 4, client_pending: 3, po_only: 2, no_country: 1 };
export function missionNotifSeverityScore(m) {
  const keys = missionNotifKeys(m);
  if (keys.length === 0) return 0;
  // Highest single severity wins; ties broken by total count.
  const max = Math.max(...keys.map((k) => NOTIF_SEVERITY[k] || 0));
  return max * 100 + keys.length;
}


function PoMissingBadge({ roles }) {
  const tooltip = roles.length === 1
    ? `Bon de commande manquant pour le rôle « ${roles[0]} »`
    : `Bon de commande manquant pour ${roles.length} rôles : ${roles.join(", ")}`;
  return (
    <span
      className={`${BADGE} bg-amber-100 text-amber-900 border-amber-300`}
      title={tooltip}
      data-testid="missionnotif-po-missing"
    >
      <AlertTriangle size={10} /> BC × {roles.length}
    </span>
  );
}

export default function MissionNotifications({ mission, className = "" }) {
  if (!mission) return null;
  // Iter-195n — Closed missions are archives, not actionable. Skip all
  // badges so they appear as a clean em-dash and never contribute to the
  // overall "needs attention" signal.
  if (mission.status === "CLOSED") {
    return <span className={`text-[10px] text-zinc-300 font-mono ${className}`} data-testid="missionnotif-none">—</span>;
  }
  const out = [];

  if (Array.isArray(mission.po_missing_roles) && mission.po_missing_roles.length > 0) {
    out.push(<PoMissingBadge key="po" roles={mission.po_missing_roles} />);
  }

  if (mission.client_status === "PENDING_VALIDATION") {
    out.push(
      <span
        key="client-pending"
        className={`${BADGE} bg-amber-100 text-amber-900 border-amber-300`}
        title="Client en attente de validation CEO"
        data-testid="missionnotif-client-pending"
      >
        <ClipboardCheck size={10} /> Client pending
      </span>
    );
  } else if (mission.client_has_contract === false) {
    if (mission.client_purchase_order_only) {
      out.push(
        <span
          key="po-only"
          className={`${BADGE} bg-blue-100 text-blue-900 border-blue-300`}
          title="Client facturé sur bon de commande uniquement"
          data-testid="missionnotif-po-only"
        >
          <FileSignature size={10} /> BC seul
        </span>
      );
    } else {
      out.push(
        <span
          key="no-contract"
          className={`${BADGE} bg-orange-100 text-orange-900 border-orange-300`}
          title="Aucun contrat cadre n'a été déposé pour ce client"
          data-testid="missionnotif-no-contract"
        >
          <FileWarning size={10} /> Contrat absent
        </span>
      );
    }
  }

  if (!mission.country || mission.country === "") {
    out.push(
      <span
        key="no-country"
        className={`${BADGE} bg-zinc-100 text-zinc-700 border-zinc-300`}
        title="Pays de la mission non renseigné — empêche la facturation"
        data-testid="missionnotif-no-country"
      >
        <Globe size={10} /> Pays
      </span>
    );
  }

  if (out.length === 0) {
    return <span className={`text-[10px] text-zinc-300 font-mono ${className}`} data-testid="missionnotif-none">—</span>;
  }
  return <div className={`flex items-center gap-1 flex-wrap ${className}`}>{out}</div>;
}
